import React from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { isPRDataEmpty } from "../../../../utils/workout-stats";
import EmptyStatementMsg from "./EmptyStatementMsg";
import PRItem from "./PRItem";
import PRRow from "./PRRow";
import { PR_TYPES } from "../../../../constants";

const PERSONAL_RECORD_TITLE_KEYS = {
	repMaxPR: "calendar.personalRecords.repMaxPR",
	volumePR: "calendar.personalRecords.volumePR",
	e1rmPR: "calendar.personalRecords.e1rmPR",
};

const PersonalRecordsCard = ({
	PRData = {},
	units = "Kg",
	isCheckIn = false,
}) => {
	const { t } = useTranslation("dashboard");
	const PRDataEmpty = isPRDataEmpty(PRData);
	const prRows = {
		repMaxPR: getRepMaxPRRow,
		volumePR: getVolumePRRow,
		e1rmPR: getE1rmPRRow,
	};

	const rows = [];
	for (const prType of PR_TYPES) {
		if (!PRData[prType.name]) {
			continue;
		}
		if (_.isEmpty(PRData[prType.name])) {
			continue;
		}
		rows.push(prRows[prType.name](prType, PRData, units, t));
	}

	return (
		<article id="personal-records-card">
			{!isCheckIn && (
				<p className="s1 mb-4">{t("calendar.personalRecords.title")}</p>
			)}
			{PRDataEmpty ? (
				<EmptyStatementMsg
					message={t("calendar.personalRecords.noDataToShow")}
				/>
			) : (
				<div className="pr-data">{rows}</div>
			)}
		</article>
	);
};

const getRepMaxPRRow = (prType, PRData, units, t) => {
	const prItems = [];
	for (const exerciseName in PRData[prType.name]) {
		for (const repRange in PRData[prType.name][exerciseName]) {
			const load = PRData[prType.name][exerciseName][repRange].load;
			const delta = PRData[prType.name][exerciseName][repRange].delta;
			prItems.push(
				<PRItem
					key={`${exerciseName}${load}${repRange}`}
					exerciseName={_.capitalize(exerciseName)}
					value={t("calendar.personalRecords.repMaxFormat", { repRange, load })}
					units={units}
					delta={delta}
				/>,
			);
		}
	}
	const title = PERSONAL_RECORD_TITLE_KEYS[prType.name]
		? t(PERSONAL_RECORD_TITLE_KEYS[prType.name])
		: prType.title;
	return (
		<PRRow key={prType.title} title={title}>
			{prItems}
		</PRRow>
	);
};

const getVolumePRRow = (prType, PRData, units, t) => {
	const prItems = [];
	for (const exerciseName in PRData[prType.name]) {
		const volume = PRData[prType.name][exerciseName].volume;
		const delta = PRData[prType.name][exerciseName].delta;
		prItems.push(
			<PRItem
				key={`${exerciseName}${volume}`}
				exerciseName={_.capitalize(exerciseName)}
				value={volume}
				units={units}
				delta={delta}
			/>,
		);
	}
	const title = PERSONAL_RECORD_TITLE_KEYS[prType.name]
		? t(PERSONAL_RECORD_TITLE_KEYS[prType.name])
		: prType.title;
	return (
		<PRRow key={prType.title} title={title}>
			{prItems}
		</PRRow>
	);
};

const getE1rmPRRow = (prType, PRData, units, t) => {
	const prItems = [];
	for (const exerciseName in PRData[prType.name]) {
		const e1rm = PRData[prType.name][exerciseName].e1rm;
		const delta = PRData[prType.name][exerciseName].delta;
		prItems.push(
			<PRItem
				key={`${exerciseName}${e1rm}`}
				exerciseName={_.capitalize(exerciseName)}
				value={e1rm}
				units={units}
				delta={delta}
			/>,
		);
	}
	const title = PERSONAL_RECORD_TITLE_KEYS[prType.name]
		? t(PERSONAL_RECORD_TITLE_KEYS[prType.name])
		: prType.title;
	return (
		<PRRow key={prType.title} title={title}>
			{prItems}
		</PRRow>
	);
};

export default PersonalRecordsCard;
